import { io, Socket } from 'socket.io-client';

// Get Socket.IO server URL from environment or use default
// Socket.IO needs to connect directly to the backend server ROOT, not through the /api proxy
// IMPORTANT: Socket.IO connects to the root namespace, not /api or any other path
const getSocketURL = (): string => {
  // Check for explicit socket URL first
  const socketUrl = (import.meta as any).env?.VITE_SOCKET_URL;
  if (socketUrl && socketUrl.trim()) {
    try {
      // Remove any path from the URL (Socket.IO uses root namespace)
      const url = new URL(socketUrl.startsWith('http') ? socketUrl : `http://${socketUrl}`);
      const baseUrl = `${url.protocol}//${url.host}`;
      console.log('[Socket] Using VITE_SOCKET_URL:', baseUrl);
      return baseUrl;
    } catch (e) {
      console.warn('[Socket] Failed to parse VITE_SOCKET_URL, falling back to default');
    }
  }

  // If VITE_API_BASE_URL is set, extract the base URL from it
  const apiUrl = (import.meta as any).env?.VITE_API_BASE_URL;
  if (apiUrl && apiUrl.trim()) {
    // If it's a full URL, extract the origin (remove any path)
    if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      try {
        const url = new URL(apiUrl);
        const baseUrl = `${url.protocol}//${url.host}`;
        console.log('[Socket] Extracted from VITE_API_BASE_URL:', baseUrl);
        return baseUrl;
      } catch (e) {
        console.warn('[Socket] Failed to parse API URL, using default');
      }
    }
    // If it's a relative path like /api, use localhost:5000 in dev
    if (apiUrl.startsWith('/')) {
      const defaultUrl = import.meta.env.DEV ? 'http://localhost:5000' : window.location.origin;
      console.log('[Socket] VITE_API_BASE_URL is relative, using:', defaultUrl);
      return defaultUrl;
    }
  }
  
  // Default: localhost:5000 in development, current origin in production
  const defaultUrl = import.meta.env.DEV ? 'http://localhost:5000' : window.location.origin;
  console.log('[Socket] Using default URL:', defaultUrl);
  return defaultUrl;
};

const SOCKET_URL = getSocketURL();
console.log('[Socket] ✅ Final resolved socket URL:', SOCKET_URL);

// Create socket instance with auto-reconnect enabled
let socket: Socket | null = null;

export const initializeSocket = (): Socket => {
  if (socket?.connected) {
    console.log('[Socket] Already connected, returning existing socket');
    return socket;
  }

  console.log('[Socket] Initializing socket connection to:', SOCKET_URL);

  // Ensure we're connecting to root namespace (no path in URL)
  // Remove trailing slashes and any paths - Socket.IO connects to root namespace
  let cleanUrl = SOCKET_URL.replace(/\/+$/, ''); // Remove trailing slashes
  
  // Ensure URL doesn't have any path segments (Socket.IO interprets paths as namespaces)
  try {
    const urlObj = new URL(cleanUrl);
    cleanUrl = `${urlObj.protocol}//${urlObj.host}`;
  } catch (e) {
    console.warn('[Socket] URL parsing failed, using as-is:', cleanUrl);
  }
  
  console.log('[Socket] Creating socket connection with config:', {
    url: cleanUrl,
    path: '/socket.io/',
    transports: ['websocket', 'polling'],
    namespace: '/', // Explicitly connect to root namespace
  });

  // Connect to root namespace explicitly (no namespace in URL)
  const ngrokHeaders = /ngrok/i.test(cleanUrl)
    ? {
        transportOptions: {
          polling: {
            extraHeaders: { 'ngrok-skip-browser-warning': 'true' },
          },
        },
      }
    : {};

  socket = io(cleanUrl, {
    path: '/socket.io/', // Explicit path for Socket.IO
    transports: ['websocket', 'polling'],
    ...ngrokHeaders,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
    withCredentials: true,
    autoConnect: true,
    forceNew: false, // Reuse existing connection if available
  });
  
  // Add additional error logging
  socket.io.on('error', (error) => {
    console.error('[Socket] IO Engine error:', error);
  });

  // Connection event handlers with logging
  socket.on('connect', () => {
    console.log('[Socket] ✅ Connected to server', {
      socketId: socket?.id,
      transport: socket?.io.engine?.transport?.name,
    });
  });

  socket.on('disconnect', (reason) => {
    console.log('[Socket] ❌ Disconnected from server', { reason });
  });

  socket.on('connect_error', (error) => {
    console.error('[Socket] 🔴 Connection error:', {
      message: error.message,
      type: (error as any)?.type,
      description: (error as any).description,
      context: (error as any).context,
      url: cleanUrl,
      fullError: error,
    });
    
    // Handle authentication errors
    if (error.message.includes('Authentication') || error.message.includes('token') || error.message.includes('Unauthorized')) {
      console.error('[Socket] 🔒 Authentication error - socket connection rejected:', error.message);
      // Don't attempt to reconnect if authentication failed
      if (socket) {
        socket.disconnect();
      }
    }
    
    // If it's an "Invalid namespace" error, log helpful info
    if (error.message.includes('Invalid namespace') || error.message.includes('namespace')) {
      console.error('[Socket] ⚠️ Namespace error detected!', {
        connectingTo: cleanUrl,
        expectedPath: '/socket.io/',
        tip: 'Make sure the URL has no path segments (e.g., use http://localhost:5000, not http://localhost:5000/api)',
      });
    }
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('[Socket] 🔄 Reconnected to server', {
      attemptNumber,
      socketId: socket?.id,
    });
  });

  socket.on('reconnect_attempt', (attemptNumber) => {
    console.log('[Socket] 🔄 Reconnection attempt:', { attemptNumber });
  });

  socket.on('reconnect_error', (error) => {
    console.error('[Socket] 🔴 Reconnection error:', {
      message: error.message,
    });
  });

  socket.on('reconnect_failed', () => {
    console.error('[Socket] 🔴 Reconnection failed - max attempts reached');
  });

  // Example: Handle pong response
  socket.on('pong', (data) => {
    console.log('[Socket] 📨 Received pong:', data);
  });

  return socket;
};

export const getSocket = (): Socket | null => {
  if (!socket) {
    console.log('[Socket] Socket not initialized, initializing now...');
    return initializeSocket();
  }
  return socket;
};

export const disconnectSocket = (): void => {
  if (socket) {
    console.log('[Socket] Disconnecting socket...');
    socket.disconnect();
    socket = null;
  }
};

export const emitEvent = (event: string, data?: any): void => {
  const currentSocket = getSocket();
  if (currentSocket && currentSocket.connected) {
    console.log('[Socket] 📤 Emitting event:', { event, data });
    currentSocket.emit(event, data);
  } else {
    console.warn('[Socket] ⚠️ Cannot emit event - socket not connected', { event });
  }
};

export const onEvent = (event: string, callback: (data: any) => void): (() => void) => {
  const currentSocket = getSocket();
  if (currentSocket) {
    console.log('[Socket] 👂 Listening to event:', event);
    currentSocket.on(event, (data) => {
      console.log('[Socket] 📥 Received event:', { event, data });
      callback(data);
    });
    // Return cleanup function
    return () => {
      if (currentSocket) {
        console.log('[Socket] 🛑 Stopped listening to event:', event);
        currentSocket.off(event);
      }
    };
  }
  return () => {};
};

export const offEvent = (event: string): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    console.log('[Socket] 🛑 Removed listener for event:', event);
    currentSocket.off(event);
  }
};

export const isConnected = (): boolean => {
  return socket?.connected || false;
};

export default socket;

